
import React, { useEffect, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { Search, Grid, Star, Sparkles, X } from 'lucide-react';
import { tools } from '@/data/tools';
import * as Dialog from '@radix-ui/react-dialog';

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Toggle the menu when ⌘K is pressed
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = (command: () => void) => {
    setOpen(false);
    command();
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] animate-in fade-in duration-300" />
        <Dialog.Content className="fixed top-[15%] left-[50%] translate-x-[-50%] w-full max-w-[640px] bg-white rounded-3xl shadow-2xl z-[101] overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-300 outline-none">
          <Command className="flex flex-col h-full max-h-[480px]">
            <div className="flex items-center border-b border-slate-100 px-4 py-4 gap-3 bg-slate-50/50">
              <Search className="text-slate-400" size={20} />
              <Command.Input
                placeholder="Search tools, categories, or utilities... (⌘K)"
                className="flex-1 bg-transparent border-none outline-none text-slate-900 placeholder:text-slate-400 font-bold"
              />
              <button 
                onClick={() => setOpen(false)}
                className="p-1 rounded-lg hover:bg-slate-200 transition-colors text-slate-400"
              >
                <X size={18} />
              </button>
            </div>

            <Command.List className="overflow-y-auto p-3 scroll-py-2 custom-scrollbar">
              <Command.Empty className="py-12 text-center text-slate-500 font-medium">
                No tools found for this search.
              </Command.Empty>

              <Command.Group heading={<span className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-3 py-2 flex items-center gap-2"><Star size={10} /> Featured Tools</span>}>
                {tools.slice(0, 5).map((tool) => (
                    <Command.Item
                        key={tool.id}
                        onSelect={() => runCommand(() => navigate(tool.route))}
                        className="flex items-center gap-4 px-3 py-3 rounded-2xl cursor-pointer hover:bg-blue-50 group transition-all aria-selected:bg-blue-50"
                    >
                        <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-transform">
                            <tool.icon size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-black text-slate-900 truncate tracking-tight">{tool.name}</span>
                            </div>
                            <p className="text-xs text-slate-500 truncate font-medium">{tool.description}</p>
                        </div>
                    </Command.Item>
                ))}
              </Command.Group>

              <div className="h-px bg-slate-100 my-2 mx-3" />

              <Command.Group heading={<span className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-3 py-2 flex items-center gap-2"><Grid size={10} /> All Utilities</span>}>
                {tools.slice(5).map((tool) => (
                    <Command.Item
                        key={tool.id}
                        onSelect={() => runCommand(() => navigate(tool.route))}
                        className="flex items-center gap-4 px-3 py-2.5 rounded-2xl cursor-pointer hover:bg-slate-50 group transition-all aria-selected:bg-slate-50"
                    >
                        <div className="w-9 h-9 rounded-xl bg-slate-50 text-slate-600 flex items-center justify-center group-hover:text-blue-600 transition-colors">
                            <tool.icon size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <span className="text-sm font-black text-slate-900 truncate tracking-tight">{tool.name}</span>
                            <p className="text-[11px] text-slate-400 truncate font-medium italic opacity-0 group-hover:opacity-100 transition-opacity">{tool.category}</p>
                        </div>
                    </Command.Item>
                ))}
              </Command.Group>
            </Command.List>

            <div className="p-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                        <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[10px] font-black text-slate-500 shadow-sm">↵</kbd>
                        <span className="text-[10px] text-slate-400 font-bold uppercase">To Select</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[10px] font-black text-slate-500 shadow-sm">↑↓</kbd>
                        <span className="text-[10px] text-slate-400 font-bold uppercase">To Navigate</span>
                    </div>
                </div>
                <div className="text-[10px] font-black text-blue-600 uppercase tracking-widest">
                    VibeTools Search
                </div>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
